/**
 * Links that were minted before the app moved (seo tickets 04, 05 and 09).
 *
 * `shared-link.ts` builds a shareable address out of the one the page is at, so
 * every `?doc=` link handed to somebody before seo ticket 04 points at
 * `https://cuekards.com/?doc=…` — which is now a static marketing page rather than
 * the application. This is the whole of what makes those links still work: the one
 * script on any static page, loaded by the landing page and by nothing else.
 *
 * **Two kinds of link were minted out of that address bar, not one**, and ticket
 * 09 is this file having forwarded only the first for four tickets.
 * `settings-url.ts` builds the other — the settings screen's "the setup in the
 * link", `setupUrlFor(window.location.href, …)`, the same construction and the
 * same consequence. A rig sent before the move is `/?wpm=…&font=…&voice=…&keys=…`,
 * and until ticket 09 it landed here and stopped: the landing page has no reader
 * for those parameters, so what the recipient got was a marketing page with the
 * setup they were sent silently gone.
 *
 * New links need none of it. They come out of `/app/` and already name it.
 *
 * **A classic script rather than a module**, built `iife` in `site.ts`. A module is
 * deferred, so the redirect would happen after the page had painted — somebody
 * following a shared link would watch a marketing page draw itself and then vanish.
 * This runs while the parser is still in the head.
 *
 * It passes `script-src 'self'` because it is same-origin, and it passes
 * `assertNoUploads` because it makes no request — `network-audit.ts` carries a
 * policy saying so, which is what stops it arriving unexamined.
 */

/**
 * The trailing slash is deliberate, twice over.
 *
 * `/app/` is the canonical address under the host's `auto-trailing-slash` and
 * `/app` is a 307 to it, so writing the bare form would cost a redirect in the
 * middle of a redirect somebody is already waiting through. It is also the
 * document's base URL, which is the fact that cost seo ticket 04 the microphone
 * fork.
 *
 * Written out rather than taken from `APP_PATH` in `scripts/hosting.ts`, and that
 * is a constraint rather than a preference: this file ships to a tab, and
 * `ships-to-the-browser.test.ts` forbids anything under `page/` importing the
 * build's own directory. So the two are held together by a test instead —
 * `landing-page.test.ts` asserts the emitted `link-through.js` contains `APP_PATH`,
 * which is the same rope `head.test.ts` uses to hold `CANONICAL_ORIGIN` to the
 * domain `wrangler.toml` deploys to.
 */
const THE_APP = "/app/";

/**
 * Every parameter that meant something at the old address (seo ticket 09).
 *
 * `doc` is `shared-link.ts`'s and the other four are `settings-url.ts`'s
 * `SETUP_PARAMS`. Both live under `page/`, so unlike `THE_APP` above this could
 * import them — and the reason it does not is **size** rather than the boundary.
 * Importing `SETUP_PARAMS` brings `@cuekards/core/overlay-defaults` into the entry
 * graph with it and takes the emitted file from 228 bytes to 508 — measured, not
 * guessed — which is a quarter of a kilobyte of settings machinery blocking the
 * parser in the head of the one page a first-time visitor sees, to save writing
 * four words down. It is held to both lists by a test instead, the same way
 * `THE_APP` is: `landing-page.test.ts` asserts the emitted `link-through.js` names
 * each of them. A fifth parameter added to either list and not to this one is a
 * fifth kind of link that quietly stops working, which is the failure that rope
 * exists to make loud.
 *
 * **It is a list rather than "any query string at all".** That is the tempting
 * one-line version and it is the wrong one: `?utm_source=…` on a link to the
 * front page would send somebody arriving from a campaign straight past the page
 * written to convince them, which is the opposite of what this page is for.
 */
const CARRIED = ["doc", "font", "wpm", "voice", "keys"];

const asked = new URLSearchParams(location.search);

if (CARRIED.some((param) => asked.has(param))) {
  // `replace` rather than `assign`: this page was never a destination, and leaving
  // it in the history would put the back button on a page that immediately sends
  // you forward again.
  location.replace(THE_APP + location.search + location.hash);
}

export {};
